import {
  CircleNotchIcon,
  DotsThreeVerticalIcon,
  TrashIcon,
  UserCircleMinusIcon,
} from '@phosphor-icons/react';
import { useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { toast } from 'sonner';
import colors from 'tailwindcss/colors';

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { useApiMutation } from '@/modules/common';

interface ParticipantTableActionsProps {
  eventId: string;
  participantId: string;
  externalId?: string;
}

export function ParticipantTableActions({
  eventId,
  externalId,
  participantId,
}: ParticipantTableActionsProps) {
  const queryClient = useQueryClient();
  const [isRemoveExternalIdDialogOpen, setIsRemoveExternalIdDialogOpen] =
    useState(false);

  const removeEventParticipantMutation = useApiMutation<void>(
    `/events/${eventId}/participants/${participantId}`,
    {
      method: 'DELETE',
    },
    {
      onError: (error) => {
        toast.error(error.message);
      },
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: ['eventDetails', String(eventId)],
        });
      },
    }
  );

  const updateParticipantMutation = useApiMutation<{
    externalId: string | null;
  }>(
    `/events/${eventId}/participants/${participantId}`,
    {
      method: 'PATCH',
    },
    {
      onSuccess: () => {
        toast.success('External ID removed successfully');
      },
      onError: (error) => {
        toast.error(error?.message);
      },
    }
  );

  const removeExternalId = async () => {
    await updateParticipantMutation.mutateAsync({
      externalId: null,
    });
    queryClient.invalidateQueries({ queryKey: ['eventParticipants'] });
  };

  const removeParticipant = async () => {
    await removeEventParticipantMutation.mutateAsync();
    queryClient.invalidateQueries({
      queryKey: ['events', eventId, 'participants'],
    });
  };

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="ghost" size="icon" data-testid="participant-actions">
            <DotsThreeVerticalIcon size={28} color={colors.zinc[400]} />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent>
          {externalId && (
            <DropdownMenuItem
              onClick={() => setIsRemoveExternalIdDialogOpen(true)}
            >
              Remove External ID
              <UserCircleMinusIcon size={28} color={colors.zinc[400]} />
            </DropdownMenuItem>
          )}
          <DropdownMenuItem
            className="text-destructive focus:text-destructive"
            onClick={removeParticipant}
          >
            Remove Participant
            <TrashIcon size={28} color="hsl(var(--destructive))" />
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>

      <AlertDialog
        open={isRemoveExternalIdDialogOpen}
        onOpenChange={setIsRemoveExternalIdDialogOpen}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              Removing the external ID will deactivate this participant’s NFC
              wristband. Are you sure you want to proceed?
            </AlertDialogTitle>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={updateParticipantMutation.isPending}>
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              disabled={updateParticipantMutation.isPending}
              onClick={removeExternalId}
              className="text-destructive border-destructive hover:text-destructive w-[150px]"
              data-testid="remove-inventory-dialog-button"
            >
              {updateParticipantMutation.isPending && (
                <CircleNotchIcon className="animate-spin" />
              )}
              Remove external ID
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
